import { supabase } from './supabase'
import { buildReport } from './reportingContract.mjs'
import { ALL_DATES } from './reportingView.mjs'
import { withVatSales } from './vatSales.mjs'

export async function readReporting(workshopId) {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 20000)
  try {
    const { data, error } = await supabase.rpc('reporting_snapshot_v1_1', { p_workshop_id: workshopId }).abortSignal(controller.signal)
    if (error || !data || new Blob([JSON.stringify(data)]).size > 4 * 1024 * 1024) throw new Error()
    let report = buildReport(data, ALL_DATES, { workshop_id: workshopId, run_id: crypto.randomUUID(), source_commit: process.env.REACT_APP_COMMIT_REF || null })
    if (process.env.REACT_APP_VAT_BUILD3 === 'true') report = withVatSales(report, data.vat, workshopId)
    return { snapshot: data, report }
  } catch { throw new Error('The complete report could not be verified. Try again; no partial figures are shown.') }
  finally { clearTimeout(timer) }
}

export class RecordingRejectedError extends Error {
  constructor(message) { super(message); this.name = 'RecordingRejectedError' }
}

export async function postOwner(path, payload) {
  const { data: { session } } = await supabase.auth.getSession()
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 20000)
  let safeError = ''
  try {
    const response = await fetch(`/.netlify/functions/${path}`, { method: 'POST', signal: controller.signal,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` }, body: JSON.stringify(payload) })
    if (response.status >= 500 || !/^application\/json\b/i.test(response.headers?.get('content-type') || '')) throw new Error()
    const result = await response.json()
    if (path === 'financial-records' && payload.operation === 'record' && !response.ok
      && [400, 401, 403, 409, 413].includes(response.status)
      && result?.recording_outcome === 'rejected_without_write' && typeof result.error === 'string' && result.error.trim()
      && Object.keys(result).every(key => ['error', 'recording_outcome'].includes(key))) {
      throw new RecordingRejectedError(result.error)
    }
    if (!response.ok || !result?.data) {
      if (path !== 'financial-records' && [400, 401, 403, 409].includes(response.status) && typeof result?.error === 'string') safeError = result.error
      throw new Error()
    }
    return result.data
  } catch (error) {
    if (error instanceof RecordingRejectedError) throw error
    throw new Error(safeError || 'Financial records could not be verified. If confirmation was attempted, retry this same confirmation safely.')
  }
  finally { clearTimeout(timer) }
}

// Compare the whole financial authority, excluding capture timestamps and display metadata.
export function financialFingerprint(snapshot) {
  return JSON.stringify(['job_details', 'jobs', 'receipts', 'allocations', 'evidence', 'financial_coverage'].map(key => snapshot[key]))
}
